<template>
  <Flex
    direction="column"
    :gap="0"
  >
    <BaseInputLabel
      v-if="!withoutLabel"
      :required="required"
      :text="$t('global.items.customer', 1)"
      size="xs"
    />
    <Transition :name="customer ? 'pop' : ''">
      <CustomerDetails
        v-if="customer"
        :customer="customer"
        :options="customerOptions"
        :hasActions="hasActions"
        :loading="loading"
        :appointmentStartAt="appointmentStartAt"
        :showAddress="requireAddress"
        :showMedicalData="requireMedicalData"
        :showAlertNotes="showAlertNotes"
        @select="onSelect"
        v-test="'customer-details'"
        :error="error"
      />
      <div
        v-else
        v-test="'customer-add'"
      >
        <BaseButtonCard
          :heading="$t('global.actions.add_customer')"
          :description="$t('global.actions.add_customer_description')"
          icon="plus"
          :loading="loading"
          :error="error"
          @click="showCreateModal = true"
        />
        <div
          v-if="error"
          class="mt-05"
          v-test="'_form-element-error'"
        >
          <BaseText
            color="error"
            size="s"
          >
            *{{ t('global.validations.required') }}
          </BaseText>
        </div>
      </div>
    </Transition>
    <Modal
      v-if="showCreateModal"
      v-bind="{ ctaTextDefault, ctaTextCreate, requireAddress, requireMedicalData }"
      @close="showCreateModal = false"
      @submit="$emit('submit', $event)"
    />
    <CreateCustomerModal
      v-if="showEditModal"
      :modalData="customer"
      :requireAddress="requireAddress"
      :requireMedicalData="requireMedicalData"
      @submit="onEditCustomer"
      @close="showEditModal = false"
    />
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useI18n } from 'vue-i18n';
import CustomerDetails from '@/components/customer/customer-details/index.vue';
import CreateCustomerModal from '@/modals/create-customer/index.vue';
import Modal from './Modal.vue';
import type { Customer, Option } from './types';

const props = withDefaults(
  defineProps<{
    appointmentStartAt?: string;
    ctaTextCreate?: string;
    ctaTextDefault?: string;
    customer?: Customer;
    loading?: boolean;
    options?: Option[];
    required?: boolean;
    error?: boolean;
    withoutLabel?: boolean;
    requireAddress?: boolean;
    requireMedicalData?: boolean;
    showAlertNotes?: boolean;
    hasActions?: boolean;
  }>(),
  {
    options: () => ['view', 'edit', 'remove'],
    withoutLabel: false,
    required: false,
    error: false,
    requireAddress: false,
    requireMedicalData: false,
    showAlertNotes: false,
    hasActions: true,
  },
);

const emit = defineEmits<{
  remove: [];
  submit: [customer: Customer];
}>();

const showCreateModal = ref(false);
const showEditModal = ref(false);

const onSelect = (option: string) => {
  if (option === 'remove') {
    emit('remove');
  } else if (option === 'edit') {
    showEditModal.value = true;
  }
};

const { t } = useI18n();

const customerOptions = computed(() =>
  props.options.map((option) => {
    if (typeof option === 'string' && option === 'view') {
      return {
        name: 'view',
        icon: 'eye',
        label: t('global.actions.view'),
        routerLink: {
          name: 'customer-overview',
          params: { customerId: props.customer.id },
        },
      };
    } else return option;
  }),
);

const onEditCustomer = (customer: Customer) => {
  emit('submit', customer);
  showEditModal.value = false;
};
</script>
